import React, { useState, useRef } from 'react';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { contactInfo } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export const Contact = () => {
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    licenseType: '',
    message: '',
    website: ''  // Honeypot field
  });
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.licenseType) {
      toast.error('Please select a program of interest.');
      return;
    }
    
    if (!recaptchaToken) {
      toast.error('Please complete the reCAPTCHA verification.');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/leads`, {
        ...formData,
        recaptchaToken
      });
      
      toast.success(response.data.message);
      
      // Reset form and reCAPTCHA
      setFormData({
        name: '',
        email: '',
        phone: '',
        licenseType: '',
        message: '',
        website: ''
      });
      setRecaptchaToken('');
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 px-4 bg-white">
      <div className="container mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a5f] mb-4">
            Get Started Today
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to start your trucking career? Fill out the form below or give us a call. We're here to answer your questions!
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl text-[#1e3a5f]">Send Us a Message</CardTitle>
              <CardDescription>Fill out the form and we'll get back to you within 24 hours.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="border-gray-300 focus:border-[#d97706] focus:ring-[#d97706]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="border-gray-300 focus:border-[#d97706] focus:ring-[#d97706]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="border-gray-300 focus:border-[#d97706] focus:ring-[#d97706]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="licenseType">Program Interest *</Label>
                  <Select
                    value={formData.licenseType}
                    onValueChange={(value) => setFormData({ ...formData, licenseType: value })}
                    required
                  >
                    <SelectTrigger className="border-gray-300 focus:border-[#d97706] focus:ring-[#d97706]">
                      <SelectValue placeholder="Select a program" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="class-a">Class A CDL</SelectItem>
                      <SelectItem value="class-b">Class B CDL</SelectItem>
                      <SelectItem value="upgrade">Class B to Class A - Upgrade</SelectItem>
                      <SelectItem value="not-sure">Not Sure Yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your goals or ask any questions..."
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="border-gray-300 focus:border-[#d97706] focus:ring-[#d97706]"
                  />
                </div>

                {/* Honeypot field - hidden from real users */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={handleChange}
                  />
                </div>

                {/* reCAPTCHA */}
                <div className="flex justify-center" data-testid="recaptcha-widget">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(token) => setRecaptchaToken(token || '')}
                    onExpired={() => setRecaptchaToken('')}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#d97706] hover:bg-[#b45309] text-white py-6 text-lg font-semibold"
                >
                  {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#d97706] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-xl text-[#1e3a5f] mb-2">Call Us Now!</h3>
                    <a href={`tel:${contactInfo.phone}`} className="text-2xl font-bold text-[#d97706] hover:underline">
                      {contactInfo.phone}
                    </a>
                    <p className="text-base text-gray-600 mt-2">Book Your Class Today!</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#d97706] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-xl text-[#1e3a5f] mb-2">Visit Us</h3>
                    <p className="text-lg text-gray-700">{contactInfo.address}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#d97706] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-xl text-[#1e3a5f] mb-2">Email Us</h3>
                    <a href="mailto:diamondtruckingschool@gmail.com" className="text-lg font-bold text-[#d97706] hover:underline break-all">
                      diamondtruckingschool@gmail.com
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1e3a5f] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-xl text-[#1e3a5f] mb-3">Hours</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-base font-semibold text-gray-700">Business Hours:</p>
                        <p className="text-base text-gray-600">{contactInfo.businessHours}</p>
                      </div>
                      <div>
                        <p className="text-base font-semibold text-gray-700">Class Hours:</p>
                        <p className="text-base text-gray-600">{contactInfo.classHours}</p>
                      </div>
                      <div>
                        <p className="text-base font-semibold text-gray-700">Weekend Hours:</p>
                        <p className="text-base text-gray-600">{contactInfo.weekendHours}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};